// sourceWizardBridge — a minimal module-level singleton that lets non-React
// modules (the action-proposal executor registry in `builtinActions.ts`) drive
// the shell-mounted `ManagedSourceWizard` imperatively.
//
// The registry runs at import time and is a plain module, so it cannot call the
// `useSourceWizard()` hook. `SourceWizardProvider` publishes its imperative
// opener here on mount; `builtinActions.ts` calls `openSourceWizard(...)`, which
// forwards to the published opener (or rejects with a clear "not mounted" error
// if the provider isn't in the tree — e.g. the flag/route is off).
//
// Same shape as Mantine's `notifications.show` bridge to its mounted
// `<Notifications />`: one live instance, imperative entry point.

/** Resolves with the created source on success, `null` on cancel/close. */
export type SourceWizardResult = { dataSourceId: string; name: string } | null;

/**
 * App-wide event fired when a source is created through the shell-mounted
 * wizard (the assistant connect handoff). Any open page that lists sources
 * (Data Management → Sources) listens for it and refetches, so a source
 * connected from the assistant appears without a manual reload. Uses the
 * repo's established window-CustomEvent bus (see OnboardingContext,
 * ChatLayoutMantine). `detail` carries the created source.
 */
export const SOURCE_CREATED_EVENT = "verity:source-created";

export function emitSourceCreated(detail: { dataSourceId: string; name: string }): void {
  window.dispatchEvent(new CustomEvent(SOURCE_CREATED_EVENT, { detail }));
}

/** Extra, optional wizard settings. Kept optional (and additive) so existing
 *  one-argument callers — the assistant handoff in `builtinActions.ts` — stay
 *  valid without change. */
export type OpenSourceWizardOptions = {
  /** Open the transform-only variant (customer brings the raw data; Verity
   *  owns the transforms only). Omitted ⇒ a fully managed pipeline. */
  transformOnly?: boolean;
};

export type OpenSourceWizard = (
  connectorType: string,
  opts?: OpenSourceWizardOptions,
) => Promise<SourceWizardResult>;

let opener: OpenSourceWizard | null = null;

/** Called by `SourceWizardProvider` on mount (and with `null` on unmount). */
export function setSourceWizardOpener(fn: OpenSourceWizard | null): void {
  opener = fn;
}

/**
 * Open the shell-mounted source wizard pre-selected for `connectorType` and
 * resolve with the created source (or `null` if cancelled). Rejects if no
 * provider is mounted so a mis-wired build fails loudly instead of silently
 * no-opping a confirm click.
 *
 * `opts` is forwarded verbatim; omitting it means "managed pipeline", which the
 * provider applies explicitly on every open (it never inherits the previous
 * open's settings).
 */
export function openSourceWizard(
  connectorType: string,
  opts?: OpenSourceWizardOptions,
): Promise<SourceWizardResult> {
  if (!opener) {
    return Promise.reject(
      new Error(
        "Source wizard is not available (SourceWizardProvider is not mounted).",
      ),
    );
  }
  return opener(connectorType, opts);
}
